import React from "react";
import {
  Building2,
  ChevronRight,
  Download,
  HelpCircle,
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
} from "lucide-react";
import type { VCard, MessagingApp } from "../../../types/vcard";
import SaveContact from "../SaveContact";
import "../BusinessPublicCard.css";

interface ExtremeKitPublicCardProps {
  vcard: VCard;
  appDownloadUrl: string;
  isSharingLocation?: boolean;
  onShareLocation: () => void;
}

type ExtremeKitData = {
  equipment?: Array<{
    name?: string;
    description?: string;
  }>;
  instagram?: string;
  socialMedia?: {
    instagram?: string;
  };
};

type CountryFlagCode = "AU" | "US" | "CA" | "GB" | "NZ";

type ContactRow = {
  key: string;
  label: string;
  value: string;
  href: string;
  icon: React.ReactNode;
  tintClassName: string;
  iconWellClassName?: string;
};

const COUNTRY_LABELS: Record<
  string,
  { flagCode: CountryFlagCode; label: string }
> = {
  australia: { flagCode: "AU", label: "Australian" },
  au: { flagCode: "AU", label: "Australian" },
  "united states": { flagCode: "US", label: "American" },
  us: { flagCode: "US", label: "American" },
  usa: { flagCode: "US", label: "American" },
  canada: { flagCode: "CA", label: "Canadian" },
  ca: { flagCode: "CA", label: "Canadian" },
  "united kingdom": { flagCode: "GB", label: "British" },
  uk: { flagCode: "GB", label: "British" },
  gb: { flagCode: "GB", label: "British" },
  "new zealand": { flagCode: "NZ", label: "New Zealander" },
  nz: { flagCode: "NZ", label: "New Zealander" },
};

const COUNTRY_DIAL_CODES: Record<string, string> = {
  australia: "+61",
  au: "+61",
  "united states": "+1",
  us: "+1",
  usa: "+1",
  canada: "+1",
  ca: "+1",
  "united kingdom": "+44",
  uk: "+44",
  gb: "+44",
  "new zealand": "+64",
  nz: "+64",
};

const MESSAGING_LABELS: Partial<Record<MessagingApp, string>> = {
  WhatsApp: "WHATSAPP",
  iMessage: "IMESSAGE",
  Messenger: "MESSENGER",
  Telegram: "TELEGRAM",
  Snapchat: "SNAPCHAT",
  WeChat: "WECHAT",
  Line: "LINE",
};

const getString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getVCardStringField = (vcard: VCard, field: string): string =>
  getString((vcard as unknown as Record<string, unknown>)[field]);

const getDialCodeForCountry = (country?: string): string =>
  COUNTRY_DIAL_CODES[(country || "").toLowerCase().trim()] || "";

const getKnownDialCodes = (): string[] =>
  Array.from(new Set(Object.values(COUNTRY_DIAL_CODES))).sort(
    (a, b) => b.length - a.length,
  );

const withDialCode = (
  phone: string,
  dialCode: string,
  country?: string,
): string => {
  const trimmed = phone.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  if (trimmed.startsWith("00")) return `+${trimmed.slice(2)}`;

  const resolvedDialCode = dialCode || getDialCodeForCountry(country);
  if (!resolvedDialCode) return trimmed;

  return `${resolvedDialCode}${trimmed.replace(/^0+/, "")}`;
};

const formatPhoneForDisplay = (phone: string): string => {
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned.startsWith("+")) return cleaned;

  const countryCode =
    getKnownDialCodes().find((code) => cleaned.startsWith(code)) ||
    cleaned.match(/^\+\d{1,3}/)?.[0];
  if (!countryCode) return cleaned;

  const body = cleaned.slice(countryCode.length);
  if (!body) return countryCode;
  return `${countryCode} ${body.match(/.{1,3}/g)?.join(" ") || body}`;
};

const CountryFlag = ({ code }: { code: CountryFlagCode }) => {
  if (code === "AU") {
    return (
      <svg
        viewBox="0 0 36 24"
        className="h-4 w-6 shrink-0 overflow-hidden rounded-[2px] shadow-sm"
        aria-label="Australian flag"
        role="img"
      >
        <rect width="36" height="24" fill="#012169" />
        <path d="M0 0h18v12H0z" fill="#012169" />
        <path d="M0 0l18 12M18 0 0 12" stroke="#fff" strokeWidth="2.4" />
        <path d="M0 0l18 12M18 0 0 12" stroke="#c8102e" strokeWidth="1.2" />
        <path d="M9 0v12M0 6h18" stroke="#fff" strokeWidth="4" />
        <path d="M9 0v12M0 6h18" stroke="#c8102e" strokeWidth="2" />
        <path
          d="m9 15 1.1 3.1 3.2-1.1-1.4 3 2.8 1.7-3.3.4.3 3.3L9 23.5l-2.7 1.9.3-3.3-3.3-.4L6.1 20l-1.4-3 3.2 1.1L9 15Z"
          fill="#fff"
          transform="scale(.55) translate(12 13)"
        />
        <circle cx="27.5" cy="5" r="1.3" fill="#fff" />
        <circle cx="31.5" cy="9" r="1.1" fill="#fff" />
        <circle cx="26" cy="13" r="1.1" fill="#fff" />
        <circle cx="31" cy="18" r="1.2" fill="#fff" />
        <circle cx="22.5" cy="18" r="1.1" fill="#fff" />
      </svg>
    );
  }

  return (
    <span className="inline-flex h-4 min-w-6 items-center justify-center rounded-[2px] bg-white/20 px-1 text-[10px] font-black text-white">
      {code}
    </span>
  );
};

const getExtremeKitData = (vcard: VCard): ExtremeKitData => {
  const info = vcard.categorySpecificInfo;
  if (!info || typeof info !== "object") return {};
  const extremeKit = (info as Record<string, unknown>).extremeKit;
  if (!extremeKit || typeof extremeKit !== "object") return {};
  return extremeKit as ExtremeKitData;
};

const formatDisplayDate = (dateValue?: string): string => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString("en-AU");
};

const getMessagingHref = (app: MessagingApp, handle: string): string => {
  const trimmedHandle = handle.trim();
  const encoded = encodeURIComponent(trimmedHandle);
  const username = trimmedHandle.replace(/^@/, "");

  if (app === "WhatsApp") {
    return `https://wa.me/${trimmedHandle.replace(/[^\d]/g, "")}`;
  }
  if (app === "Telegram") {
    return `https://t.me/${username}`;
  }
  if (app === "Messenger") {
    return `https://m.me/${username}`;
  }
  if (app === "Snapchat") {
    return `https://www.snapchat.com/add/${username}`;
  }
  if (app === "Line") {
    return `https://line.me/R/ti/p/~${username}`;
  }
  if (app === "WeChat") {
    return `weixin://dl/chat?${encoded}`;
  }
  return `sms:${trimmedHandle}`;
};

const getInstagramHref = (handle: string): string => {
  if (/^https?:\/\//i.test(handle)) return handle;
  return `https://instagram.com/${handle.replace(/^@/, "")}`;
};

const getMapsHref = (hotelInfo: VCard["hotelInfo"]): string => {
  if (!hotelInfo) return "";
  const address = [
    hotelInfo.name,
    hotelInfo.address.line1,
    hotelInfo.address.line2,
    hotelInfo.address.city,
    hotelInfo.address.state,
    hotelInfo.address.zip,
    hotelInfo.address.country,
  ]
    .filter(Boolean)
    .join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    address,
  )}`;
};

const ExtremeKitPublicCard = ({
  vcard,
  appDownloadUrl,
  isSharingLocation = false,
  onShareLocation,
}: ExtremeKitPublicCardProps) => {
  const extremeKitData = getExtremeKitData(vcard);
  const displayName =
    `${vcard.firstName || ""} ${vcard.lastName || ""}`.trim() ||
    vcard.formattedName ||
    "TAGiFY-iT Owner";
  const photoUrl =
    typeof vcard.photo === "string" && vcard.photo.length > 0
      ? vcard.photo
      : null;
  const initials = `${vcard.firstName?.[0] || ""}${vcard.lastName?.[0] || ""}`;
  const country =
    COUNTRY_LABELS[(vcard.address?.country || "").toLowerCase().trim()] ?? null;
  const rawPhone = vcard.mobilePhone || vcard.phone || vcard.workPhone || "";
  const phoneDialCode = vcard.mobilePhone
    ? getVCardStringField(vcard, "mobilePhoneCountryCode")
    : vcard.workPhone
      ? getVCardStringField(vcard, "workPhoneCountryCode")
      : "";
  const phone = withDialCode(rawPhone, phoneDialCode, vcard.address?.country);
  const displayPhone = formatPhoneForDisplay(phone);
  const email = vcard.email || vcard.personalEmail || vcard.workEmail || "";
  const instagram =
    getString(extremeKitData.instagram) ||
    getString(extremeKitData.socialMedia?.instagram);
  const taggedItems =
    extremeKitData.equipment
      ?.map((item) => getString(item?.name) || getString(item?.description))
      .filter(Boolean) ?? [];
  const contactRows: ContactRow[] = [];

  if (phone) {
    contactRows.push({
      key: "phone",
      label: "CALL OR TEXT",
      value: displayPhone,
      href: `tel:${phone}`,
      icon: <Phone className="h-5 w-5 text-red-600" />,
      tintClassName: "bg-red-100",
    });
  }

  vcard.messagingContacts?.forEach((contact) => {
    if (!contact.handle) return;
    contactRows.push({
      key: `${contact.app}-${contact.handle}`,
      label: MESSAGING_LABELS[contact.app] || contact.app.toUpperCase(),
      value: "Send Message",
      href: getMessagingHref(contact.app, contact.handle),
      icon: <MessageCircle className="h-5 w-5 text-green-600" />,
      tintClassName: "bg-green-100",
    });
  });

  if (email) {
    contactRows.push({
      key: "email",
      label: "EMAIL",
      value: email,
      href: `mailto:${email}`,
      icon: <Mail className="h-5 w-5 text-red-600" />,
      tintClassName: "bg-red-100",
    });
  }

  if (instagram) {
    contactRows.push({
      key: "instagram",
      label: "INSTAGRAM",
      value: "Send Message",
      href: getInstagramHref(instagram),
      icon: <Instagram className="h-5 w-5 text-pink-600" />,
      tintClassName: "bg-pink-100",
    });
  }

  contactRows.push({
    key: "anonymous",
    label: "ANONYMOUS",
    value: "Contact Anonymously with TAGiFY-iT",
    href: `/contact?userId=${encodeURIComponent(vcard.userId)}&category=extremeKit`,
    icon: <img src="/TAGiF-iT_Color_SVG.svg" alt="" className="h-6 w-6" />,
    tintClassName: "",
    iconWellClassName: "border border-red-500 bg-transparent",
  });

  contactRows.push({
    key: "direct-messaging",
    label: "DIRECT MESSAGING",
    value: "Coming Soon Direct Messaging",
    href: "#",
    icon: <img src="/TAGiF-iT_Color_SVG.svg" alt="" className="h-6 w-6" />,
    tintClassName: "",
    iconWellClassName: "border border-green-500 bg-transparent",
  });

  return (
    <div className="bpc-page">
      <div className="bpc-outer-card">
        <div className="bpc-header">
          <div className="relative px-5 pb-8 pt-7">
            <a
              href="/about"
              className="absolute right-4 top-4 flex items-center gap-1.5 opacity-85 transition-opacity hover:opacity-100"
              title="What is TAGiFY-iT?"
            >
              <img
                src="/TAGiF-iT_Color_SVG.svg"
                alt="TAGiFY-iT"
                className="h-7 w-auto"
              />
              <span className="text-[11px] font-semibold tracking-wide text-white">
                TAGiFY-iT
              </span>
            </a>

            <div className="mt-4 flex items-center gap-5 pr-20">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={displayName}
                  className="bpc-photo h-24 w-24 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="bpc-photo flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-white">
                  <span className="text-3xl font-bold text-red-600">
                    {initials || "TG"}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold leading-tight text-white">
                  {displayName}
                </h1>
                <p className="mt-1 text-base font-medium text-red-50">
                  Gear Owner
                </p>
                {country && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-white">
                    <CountryFlag code={country.flagCode} />
                    {country.label}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-4 pb-6 pt-4">
          <div className="bpc-card space-y-3 rounded-3xl px-4 py-4">
            <button
              type="button"
              onClick={onShareLocation}
              disabled={isSharingLocation}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-red-600 py-3.5 text-base font-bold tracking-wide text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MapPin className="h-5 w-5 text-white" />
              {isSharingLocation
                ? "Sharing Location..."
                : "Share Extreme Gear Location"}
            </button>
          </div>

          <div className="bpc-card rounded-3xl px-5 py-4">
            {contactRows.map((row, index) => (
              <React.Fragment key={row.key}>
                {index > 0 && <div className="bpc-divider my-3" />}
                <a
                  href={row.href}
                  onClick={
                    row.href === "#"
                      ? (event) => event.preventDefault()
                      : undefined
                  }
                  target={row.href.startsWith("http") ? "_blank" : undefined}
                  rel={
                    row.href.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                  aria-disabled={row.href === "#"}
                  className={`flex items-center gap-3 text-left transition ${
                    row.href === "#" ? "cursor-default" : "hover:opacity-80"
                  }`}
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                      row.iconWellClassName || row.tintClassName
                    }`}
                  >
                    {row.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-bold tracking-wide text-gray-500">
                      {row.label}
                    </span>
                    <span className="block truncate font-medium text-gray-800">
                      {row.value}
                    </span>
                  </span>
                  <ChevronRight className="h-5 w-5 shrink-0 text-gray-300" />
                </a>
              </React.Fragment>
            ))}
          </div>

          {vcard.hotelInfo && (
            <div className="bpc-card rounded-2xl px-4 py-3">
              <div className="mb-1 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h2 className="text-sm font-black uppercase tracking-wide text-gray-800">
                  Hotel Details
                </h2>
                <div className="bpc-divider flex-1" />
              </div>
              <p className="font-semibold text-gray-900">
                {vcard.hotelInfo.name}
              </p>
              {vcard.hotelInfo.phone && (
                <a
                  href={`tel:${vcard.hotelInfo.phone}`}
                  className="block text-sm text-gray-600"
                >
                  {vcard.hotelInfo.phone}
                </a>
              )}
              <div className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 text-sm text-gray-600">
                <div>
                  {vcard.hotelInfo.checkIn && (
                    <p>
                      Check-in&nbsp;{" "}
                      {formatDisplayDate(vcard.hotelInfo.checkIn)}
                    </p>
                  )}
                  {vcard.hotelInfo.checkOut && (
                    <p>
                      Check-out {formatDisplayDate(vcard.hotelInfo.checkOut)}
                    </p>
                  )}
                </div>
                <a
                  href={getMapsHref(vcard.hotelInfo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="self-end whitespace-nowrap text-blue-600 hover:underline"
                >
                  Open in Maps ›
                </a>
              </div>
            </div>
          )}

          <a
            href={appDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bpc-btn-neu flex items-center justify-between rounded-2xl px-5 py-4 text-gray-600 transition-colors hover:text-gray-900"
          >
            <Download className="h-5 w-5 shrink-0 text-red-600" />
            <div className="flex items-center gap-2">
              <img
                src="/TAGiF-iT_Color_SVG.svg"
                alt="TAGiFY-iT"
                className="h-5 w-auto"
              />
              <span className="font-medium">Download TAGiFY-iT App</span>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
          </a>

          <SaveContact
            vcard={vcard}
            label="SAVE CONTACT"
            className="bpc-btn-neu flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-sm font-semibold tracking-wide text-gray-900 transition hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
          />

          <div className="text-center">
            {taggedItems.length > 0 ? (
              <div className="text-sm font-medium text-gray-500">
                <p>Tagged with TAGiFY-iT</p>
                <ul className="mt-1 flex flex-wrap justify-center gap-x-2 gap-y-1">
                  {taggedItems.map((item) => (
                    <li key={item} className="max-w-full truncate">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm font-medium text-gray-500">
                Extreme Gear Tagged with TAGiFY-iT
              </p>
            )}
            <a
              href="/about"
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-red-600 underline underline-offset-4"
            >
              <HelpCircle className="h-4 w-4" />
              What is TAGiFY-iT?
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtremeKitPublicCard;
